import { Box, Checkbox, Divider, MenuItem } from "@mui/material"
import { memo } from "react"
import { Controller, useFormContext } from "react-hook-form"

import MultiSelect from "@/components/MultiSelect/MultiSelect"
import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES } from "@/constants/forms"
import type { SelectOption } from "@/types/form-fields"

interface MultipleSelectWithSelectAllProps {
  name: string
  label: string
  options: SelectOption[]
}

const SELECT_ALL_VALUE = "SELECT_ALL"

/**
 * @deprecated This component is deprecated. Use `AerosMultiSelect` with `withSelectAll` prop from `@/components/UiKit/AerosMultiSelect` instead.
 * For React Hook Form integration, use `FormMultiSelect` with `withSelectAll` prop from `@/components/UiKit/FormControls/FormMultiSelect`.
 */
const MultiSelectWithSelectAll = ({ name, label, options }: MultipleSelectWithSelectAllProps) => {
  const { control, watch } = useFormContext()
  const fieldValues = watch(name)
  const isSelectedAll = fieldValues.length === options.length
  const isIndeterminate = Boolean(!isSelectedAll && fieldValues.length)

  const handleChange = (field: { value: string[]; onChange: (value: string[]) => void }, value: string[]) => {
    if ((value.includes(SELECT_ALL_VALUE) && isSelectedAll) || (value.includes(SELECT_ALL_VALUE) && isIndeterminate)) {
      return field.onChange([])
    }
    if (value.includes(SELECT_ALL_VALUE)) {
      return field.onChange(options.map((option) => option.value))
    }
    return field.onChange(value)
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box className='mt-6' data-testid={`instructions-form-${name}`}>
          <label htmlFor={name} className={FIELD_LABEL_CLASSES}>
            {label}
            <span className='text-red-600'>*</span>
          </label>
          <MultiSelect
            id={name + "-select"}
            name={field.name}
            value={field.value?.map(String) ?? []}
            onChange={(value) => handleChange(field, value)}
            options={options}
            error={!!error}
          >
            <MenuItem key={field.name + "-select-all"} value={SELECT_ALL_VALUE}>
              <Checkbox
                key={field.name + "-select-all-checkbox"}
                indeterminate={isIndeterminate}
                checked={isSelectedAll}
              />
              Select All
            </MenuItem>

            <Divider className='my-0' />
            {options.map(({ value, label }) => (
              <MenuItem key={field.name + value} value={value} className={"pl-8"}>
                <Checkbox checked={field.value?.map(String).includes(value)} />
                {label}
              </MenuItem>
            ))}
          </MultiSelect>
          {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
        </Box>
      )}
    />
  )
}

export default memo(MultiSelectWithSelectAll)
